import { refreshAccessToken } from './helpers';
import { csrfService } from '../services/csrfService';
import { shouldAddWhiteLabelHeader, getCurrentDomain } from './domainUtils';

// Cache for storing API responses
const apiCache = new Map();

// Map for tracking ongoing requests to prevent duplicates
const ongoingRequests = new Map();

// Single-flight refresh to avoid multiple parallel refresh calls
let refreshInFlight = null;
const ensureRefreshed = async () => {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

/**
 * Enhanced fetch function with authentication, CSRF protection, caching, and error handling
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {boolean} useCache - Whether to use cache for this request
 * @param {number} cacheDuration - How long to cache the response in milliseconds
 * @returns {Promise<any>} - The response data
 */
export const apiFetch = async (url, options = {}, useCache = false, cacheDuration = 60000) => {
  // Get HTTP method
  const method = options.method || 'GET';

  // Prepare base headers - authentication is handled via cookies
  let headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add CSRF token for state-changing requests (POST, PUT, DELETE, PATCH)
  headers = await csrfService.getHeaders(method, headers);

  // Add white label domain header for whitelabel subdomains AND custom domains
  if (shouldAddWhiteLabelHeader()) {
    headers['X-Original-Host'] = getCurrentDomain();
  }

  // Prepare the complete options
  const fetchOptions = {
    credentials: 'include',
    ...options,
    headers,
  };

  // Create a cache key based on the URL and options
  const cacheKey = useCache ? `${url}-${JSON.stringify(fetchOptions)}` : null;

  // Create a request key for deduplication (includes method and body for uniqueness)
  const requestKey = `${fetchOptions.method || 'GET'}-${url}-${JSON.stringify(fetchOptions.body || {})}`;

  // Check if we have a cached response and it's not expired
  if (useCache && cacheKey && apiCache.has(cacheKey)) {
    const cachedData = apiCache.get(cacheKey);
    if (cachedData.expiry > Date.now()) {
      return cachedData.data;
    }
    // Remove expired cache entry
    apiCache.delete(cacheKey);
  }

  // Optional request deduplication: only for GET requests when using cache and not explicitly disabled
  const noDedupe = options.noDedupe === true;
  const shouldDedupe = !noDedupe && (method === 'GET') && useCache;
  if (shouldDedupe && ongoingRequests.has(requestKey)) {
    console.log('Request deduplication: returning existing promise for', requestKey);
    return ongoingRequests.get(requestKey);
  }

  // Create the request promise
  const requestPromise = (async () => {
    try {
      const response = await fetch(url, fetchOptions);

      // Handle CSRF token errors (403)
      if (response.status === 403) {
        const shouldRetry = await csrfService.handleError(response);
        if (shouldRetry) {
          // Refresh CSRF token in headers and retry
          const newHeaders = await csrfService.getHeaders(method, headers);
          const retryOptions = {
            ...fetchOptions,
            headers: newHeaders
          };

          const retryResponse = await fetch(url, retryOptions);

          if (!retryResponse.ok) {
            let errorMessage = `API error: ${retryResponse.status}`;
            try {
              const errorData = await retryResponse.json();
              errorMessage = errorData.message || errorMessage;
            } catch (e) {
              // If we can't parse the error response, use the default message
            }

            const error = new Error(errorMessage);
            error.status = retryResponse.status;
            error.response = retryResponse;
            throw error;
          }

          let data;
          try {
            const text = await retryResponse.text();
            data = text ? JSON.parse(text) : null;
          } catch (e) {
            data = null;
          }

          // Cache the successful response if caching is enabled
          if (useCache && cacheKey) {
            apiCache.set(cacheKey, {
              data,
              expiry: Date.now() + cacheDuration,
            });
          }

          return data;
        }
      }

      // Handle authentication errors
      if (response.status === 401) {
        // Try to refresh the token (single-flight)
        const refreshed = await ensureRefreshed();
        if (refreshed) {
          // Retry the request - token is now in cookies
          // Also refresh CSRF token
          const newHeaders = await csrfService.getHeaders(method, headers);
          const retryOptions = {
            ...fetchOptions,
            headers: newHeaders
          };

          const retryResponse = await fetch(url, retryOptions);

          if (!retryResponse.ok) {
            let errorMessage = `API error: ${retryResponse.status}`;
            try {
              const errorData = await retryResponse.json();
              errorMessage = errorData.message || errorMessage;
            } catch (e) {
              // If we can't parse the error response, use the default message
            }

            const error = new Error(errorMessage);
            error.status = retryResponse.status;
            error.response = retryResponse;
            throw error;
          }

          let data;
          try {
            const text = await retryResponse.text();
            data = text ? JSON.parse(text) : null;
          } catch (e) {
            data = null;
          }

          // Cache the successful response if caching is enabled
          if (useCache && cacheKey) {
            apiCache.set(cacheKey, {
              data,
              expiry: Date.now() + cacheDuration,
            });
          }

          return data;
        } else {
          // If token refresh failed, redirect to login
          window.location.href = '/login';
          throw new Error('Authentication failed');
        }
      }

      // Check for other errors
      if (!response.ok) {
        let errorMessage = `API error: ${response.status}`;
        let errorData = null;
        try {
          errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If we can't parse the error response, use the default message
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        error.response = response;
        error.data = errorData;
        throw error;
      }

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        data = null;
      }

      // Cache the successful response if caching is enabled
      if (useCache && cacheKey) {
        apiCache.set(cacheKey, {
          data,
          expiry: Date.now() + cacheDuration,
        });
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  })();

  // Store the ongoing request only when deduplication is enabled
  if (typeof shouldDedupe !== 'undefined' && shouldDedupe) {
    ongoingRequests.set(requestKey, requestPromise);

    // Clean up the ongoing request when it completes (success or failure)
    requestPromise.finally(() => {
      ongoingRequests.delete(requestKey);
    });
  }

  return requestPromise;
};

/**
 * Clear all cached API responses
 */
export const clearApiCache = () => {
  apiCache.clear();
};

/**
 * Clear ongoing requests (useful for cleanup or testing)
 */
export const clearOngoingRequests = () => {
  ongoingRequests.clear();
};

/**
 * Get information about ongoing requests (for debugging)
 */
export const getOngoingRequestsInfo = () => {
  return {
    count: ongoingRequests.size,
    requests: Array.from(ongoingRequests.keys()),
  };
};

/**
 * Clear specific cached API response
 * @param {string} url - The URL to clear from cache
 */
export const clearApiCacheForUrl = (url) => {
  for (const key of apiCache.keys()) {
    if (key.startsWith(url)) {
      apiCache.delete(key);
    }
  }
};

/**
 * API methods for common operations
 */
export const api = {
  get: (url, options = {}, useCache = false, cacheDuration = 60000) =>
    apiFetch(url, { ...options, method: 'GET' }, useCache, cacheDuration),

  post: (url, data, options = {}) =>
    apiFetch(url, { ...options, method: 'POST', body: JSON.stringify(data) }),

  put: (url, data, options = {}) =>
    apiFetch(url, { ...options, method: 'PUT', body: JSON.stringify(data) }),

  patch: (url, data, options = {}) =>
    apiFetch(url, { ...options, method: 'PATCH', body: JSON.stringify(data) }),

  delete: (url, options = {}) =>
    apiFetch(url, { ...options, method: 'DELETE' }),
};

/**
 * Enhanced fetch wrapper with CSRF protection
 * Use this instead of native fetch() for all API calls
 *
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} - The fetch response
 *
 * @example
 * const response = await fetchWithCSRF('/v1/cards', {
 *   method: 'POST',
 *   credentials: 'include',
 *   headers: { 'Content-Type': 'application/json' },
 *   body: JSON.stringify(data)
 * });
 */
export const fetchWithCSRF = async (url, options = {}) => {
  const method = options.method || 'GET';

  // Get headers with CSRF token
  const headers = await csrfService.getHeaders(method, options.headers || {});

  // Merge headers into options and ensure credentials are included by default
  const fetchOptions = {
    credentials: 'include',
    ...options,
    headers
  };

  const doFetch = async (opts) => fetch(url, opts);

  let response = await doFetch(fetchOptions);

  // Handle CSRF token errors (403)
  if (response.status === 403) {
    const shouldRetry = await csrfService.handleError(response);
    if (shouldRetry) {
      const newHeaders = await csrfService.getHeaders(method, headers);
      response = await doFetch({
        ...fetchOptions,
        headers: newHeaders
      });
    }
  }

  // Handle authentication errors (401)
  if (response.status === 401) {
    const refreshed = await ensureRefreshed();
    if (refreshed) {
      const newHeaders = await csrfService.getHeaders(method, headers);
      response = await doFetch({
        ...fetchOptions,
        headers: newHeaders
      });
    } else {
      // If token refresh failed, redirect to login for a clean re-auth
      window.location.href = '/login';
      throw new Error('Authentication failed');
    }
  }

  return response;
};
